import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from 'Text';
import { type StreamForStreamDashboard, type LessonForStreamDashboard } from 'Lessons';
import { type StreamSummaryLink, type ShareAndBookmark, type GetLessonAccess } from '../StreamDashboard.types';
import {
    getComingSoonLessons,
    getKeepLearningLesson,
    getLessonLaunchTextKey,
    getOrderedLessons,
    getStreamImageSrc,
    getChapterIndexForLesson,
    getChapterLessonsIndex,
    hasLessonProgress,
    hasTimeLimit,
    isCompleteExcludingComingSoonLessons,
    isStreamComplete,
    isStreamInProgress,
    isStreamStarted,
} from '../selectors';
import { ChapterList } from './ChapterList';
import { CourseDescriptionBox } from './CourseDescriptionBox';
import { StartCourseBox } from './StartCourseBox';
import { NextUpBox } from './NextUpBox';
import { TimerBox } from './TimerBox';
import { ExamScheduleBox } from './ExamScheduleBox';
import { ShareAndBookmarkRow } from './ShareAndBookmarkRow';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    The left-hand column: the certificate on small screens, the course description, the exam boxes, the
    start or next-up call to action, and the chapter accordion.

    The exam boxes appear twice on the page, here below md and in the sidebar above it. That is how the
    old template did it, and it is a layout decision rather than a duplication: the sidebar does not
    exist as a column on a phone.
*/

type Props = {
    stream: StreamForStreamDashboard;
    isSignedIn: boolean;
    mustVerifyIdentity: boolean;
    hasExamVerificationRecord: boolean;
    canLaunchStream: boolean;
    canLaunchLesson: (lesson: LessonForStreamDashboard) => boolean;
    formattedExamOpenTime: string | null;
    isMobile: boolean;

    // The chapter list carried ng-if="!hidingExtraContentFromMobile" on main.
    deferChapterList: boolean;
    timeLimitHours: number | null;
    showSchedule: boolean;
    onShowSchedule: () => void;
    summariesForLesson: (lesson: LessonForStreamDashboard) => StreamSummaryLink[];
    getLessonAccess: GetLessonAccess;
    summariesDisabled: boolean;
    onLaunchLesson: (lesson: LessonForStreamDashboard, linkId: string) => void;
    onLockedLesson: (reasonMessage: string) => void;
    onSummaryClick: (summaryId: string) => void;
    certificate: ReactNode;

    /*
        The share strip and bookmark button that share a row between sm and md. main keeps a second copy
        of each in the sidebar for md and up; these are the tablet-band ones.
    */
    shareAndBookmark: ShareAndBookmark;
};

/*
    The timer and the schedule, which only an exam stream shows. Extracted from
    StreamDashboardMainColumn to keep that function inside the 100-line lint cap; the two boxes have
    no behaviour of their own and always render together in the main column.
*/
const MainColumnExamBoxes: FC<{
    stream: StreamForStreamDashboard;
    showExamBoxes: boolean;
    timeLimitHours: number | null;
    formattedExamOpenTime: string | null;
    showSchedule: boolean;
    onShowSchedule: () => void;
}> = ({ stream, showExamBoxes, timeLimitHours, formattedExamOpenTime, showSchedule, onShowSchedule }) => (
    <>
        {showExamBoxes && (
            <TimerBox
                timeRunsOutAt={stream.lessonStreamsProgress?.timeRunsOutAt ?? null}
                timeLimitHours={timeLimitHours}
                hasStarted={isStreamStarted(stream)}
                isInProgress={isStreamInProgress(stream)}
                className="mb-7.5 md:hidden"
            />
        )}

        <ExamScheduleBox
            formattedExamOpenTime={formattedExamOpenTime}
            timeLimitHours={timeLimitHours}
            showSchedule={showSchedule}
            onShowSchedule={onShowSchedule}
            className="mb-7.5 md:hidden"
        />
    </>
);

/* Every published beta lesson is finished, but the course itself is not complete yet. */
const ComingSoonNotice: FC<{ stream: StreamForStreamDashboard }> = ({ stream }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    const show =
        getComingSoonLessons(stream).length > 0 &&
        isCompleteExcludingComingSoonLessons(stream) &&
        !isStreamComplete(stream);
    if (!show) return null;

    return (
        <Text as="p" className="mb-7.5 text-md text-slate-grey">
            {t('coming_soon_come_back_later')}
        </Text>
    );
};

export const StreamDashboardMainColumn: FC<Props> = ({
    stream,
    isSignedIn,
    mustVerifyIdentity,
    hasExamVerificationRecord,
    canLaunchStream,
    canLaunchLesson,
    formattedExamOpenTime,
    isMobile,
    deferChapterList,
    timeLimitHours,
    showSchedule,
    onShowSchedule,
    summariesForLesson,
    getLessonAccess,
    summariesDisabled,
    onLaunchLesson,
    onLockedLesson,
    onSummaryClick,
    certificate,
    shareAndBookmark,
}) => {
    const keepLearningLesson = getKeepLearningLesson(stream, canLaunchLesson);
    const showExamBoxes = hasTimeLimit(stream) && !isStreamComplete(stream);
    const showStartBox = !hasLessonProgress(stream) || mustVerifyIdentity;
    const showNextUp = hasLessonProgress(stream) && !mustVerifyIdentity && !!keepLearningLesson;

    return (
        <div className="w-full md:w-2/3">
            <div className="md:hidden">{certificate}</div>

            <CourseDescriptionBox
                title={stream.title}
                description={stream.description}
                imageSrc={getStreamImageSrc(stream) ?? ''}
                isBeta={stream.beta}
                // Nothing below it on a phone, 15px from sm up. The box carries its own bottom padding.
                className="sm:mb-3.5"
            />

            <MainColumnExamBoxes
                stream={stream}
                showExamBoxes={showExamBoxes}
                timeLimitHours={timeLimitHours}
                formattedExamOpenTime={formattedExamOpenTime}
                showSchedule={showSchedule}
                onShowSchedule={onShowSchedule}
            />

            {showStartBox && (
                <StartCourseBox
                    mustVerifyIdentity={mustVerifyIdentity}
                    isExam={stream.exam}
                    hasLessonProgress={hasLessonProgress(stream)}
                    hasExamVerificationRecord={hasExamVerificationRecord}
                    canLaunch={canLaunchStream}
                    onStart={() => onLaunchLesson(getOrderedLessons(stream)[0], 'stream_dashboard_start_course')}
                    className="mb-7.5"
                />
            )}

            {showNextUp && (
                <NextUpBox
                    lessonTitle={keepLearningLesson.title}
                    chapterNumber={(getChapterIndexForLesson(stream, keepLearningLesson) ?? 0) + 1}
                    lessonNumber={(getChapterLessonsIndex(stream, keepLearningLesson) ?? 0) + 1}
                    launchTextKey={getLessonLaunchTextKey(keepLearningLesson)}
                    onLaunch={() =>
                        onLaunchLesson(
                            keepLearningLesson,
                            `keep_learning_${getLessonLaunchTextKey(keepLearningLesson)}`,
                        )
                    }
                    className="mb-7.5"
                />
            )}

            <ShareAndBookmarkRow
                {...shareAndBookmark}
                canShare={shareAndBookmark.canShare && !stream.exam}
                className="mb-7.5"
            />

            <ComingSoonNotice stream={stream} />

            {!deferChapterList && (
                <ChapterList
                    stream={stream}
                    isSignedIn={isSignedIn}
                    isMobile={isMobile}
                    summariesForLesson={summariesForLesson}
                    getLessonAccess={getLessonAccess}
                    summariesDisabled={summariesDisabled}
                    onLaunchLesson={lesson =>
                        onLaunchLesson(lesson, `stream_dashboard_${getLessonLaunchTextKey(lesson)}`)
                    }
                    onLockedLesson={onLockedLesson}
                    onSummaryClick={onSummaryClick}
                />
            )}
        </div>
    );
};
